import { XCHAIN_IMPORTED_DEPOSITS_KEY } from "@/constants/local-storage-keys";
import { boundedKeySet } from "@/hooks/storage/bounded-key-set";

import type { TransferProgressData } from "./transfer-progress-card";
import { isStrandedXchainDeposit } from "./transfer-terminality";

/**
 * Per owner wallet: which deliveries have already been turned into a card.
 *
 * Keyed on the server's delivery `id` — the destination-chain transaction hash — and deliberately
 * not on the deposit address: one address funds an Intents account that accrues every deposit the
 * user ever makes, so an address-keyed ledger would record "already dealt with" against the
 * account and suppress every later deposit into it. The hash works as a key because a withdrawal
 * is never observable without one, so there is no window to stand in for and no key to firm up
 * later.
 *
 * Written by the tracker as it imports, and released by `forgetDismissedDelivery` below — the two
 * sides of "has this delivery been dealt with", which is why the ledger lives here rather than in
 * either of them.
 */
export const importedDeposits = boundedKeySet(XCHAIN_IMPORTED_DEPOSITS_KEY, 200);

/**
 * Releases a dismissed card's delivery, so the tracker may build another one for it.
 *
 * Only for a card stranded at a step nothing transitions: that card was created from a delivery
 * the server could not classify, and an unclassified delivery may still turn into a real one. If
 * it does, the tracker has to be free to show it — with its move into cross margin, which no other
 * surface offers.
 *
 * Every other dismissal keeps its entry. The server reports a settled delivery forever, so
 * forgetting a card the user has finished with is what would put it straight back on the next
 * poll.
 *
 * The one visible consequence of a card returning is a second
 * `analytics.portfolio.xchainDeposit.detected` for the same delivery, which is accurate: it really
 * was detected again, this time as something the user can act on.
 */
export function forgetDismissedDelivery(
  transfer: TransferProgressData,
  ownerWalletAddress: string | undefined,
): void {
  if (!ownerWalletAddress) return;
  if (!isStrandedXchainDeposit(transfer)) return;
  importedDeposits(ownerWalletAddress).remove(transfer.deliveryId);
}
